"use client"

import React from "react"
import { useState, useEffect } from "react"
import { X, Clock, Car, CreditCard } from "lucide-react"
import { Dialog, Flex, Box, Heading, Text, TextField, Select, Button, ScrollArea } from "@radix-ui/themes"

interface Captain {
  _id?: string
  name: string
  email: string
  phone: string
  profileImage: string
  vehicleType: string
  vehicleNumber: string
  drivingLicense: string
  vehicleRegistration: string
  insurance: string
  bankDetails: {
    accountNumber: string
    routingNumber: string
    accountHolderName: string
  }
  workingHours: {
    [key: string]: { start: string; end: string; isWorking: boolean }
  }
}

interface CaptainModalProps {
  captain?: Captain | null
  onClose: () => void
  onSave: (data: any) => void
}

const CaptainModal: React.FC<CaptainModalProps> = ({ captain, onClose, onSave }) => {
  const [formData, setFormData] = useState<{
    name: string
    email: string
    phone: string
    profileImage: string
    vehicleType: string
    vehicleNumber: string
    drivingLicense: string
    vehicleRegistration: string
    insurance: string
    bankDetails: {
      accountNumber: string
      routingNumber: string
      accountHolderName: string
    }
    workingHours: {
      [key: string]: { start: string; end: string; isWorking: boolean }
    }
  }>({
    name: "",
    email: "",
    phone: "",
    profileImage: "",
    vehicleType: "motorcycle",
    vehicleNumber: "",
    drivingLicense: "",
    vehicleRegistration: "",
    insurance: "",
    bankDetails: {
      accountNumber: "",
      routingNumber: "",
      accountHolderName: "",
    },
    workingHours: {
      monday: { start: "09:00", end: "18:00", isWorking: true },
      tuesday: { start: "09:00", end: "18:00", isWorking: true },
      wednesday: { start: "09:00", end: "18:00", isWorking: true },
      thursday: { start: "09:00", end: "18:00", isWorking: true },
      friday: { start: "09:00", end: "18:00", isWorking: true },
      saturday: { start: "09:00", end: "18:00", isWorking: true },
      sunday: { start: "09:00", end: "18:00", isWorking: false },
    },
  })

  const vehicleTypes = ["motorcycle", "bicycle", "car", "scooter"]
  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

  useEffect(() => {
    if (captain) {
      setFormData({
        name: captain.name || "",
        email: captain.email || "",
        phone: captain.phone || "",
        profileImage: captain.profileImage || "",
        vehicleType: captain.vehicleType || "motorcycle",
        vehicleNumber: captain.vehicleNumber || "",
        drivingLicense: captain.drivingLicense || "",
        vehicleRegistration: captain.vehicleRegistration || "",
        insurance: captain.insurance || "",
        bankDetails: captain.bankDetails || {
          accountNumber: "",
          routingNumber: "",
          accountHolderName: "",
        },
        workingHours: captain.workingHours || formData.workingHours,
      })
    }
  }, [captain])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleBankDetailsChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [field]: value,
      },
    }))
  }

  const handleWorkingHoursChange = (day: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          [field]: value,
        },
      },
    }))
  }

  return (
    <Dialog.Root open={true} onOpenChange={onClose}>
      <Dialog.Content style={{ maxWidth: 900 }}>
        <Dialog.Title>
          {captain ? "Edit Captain" : "Add New Captain"}
        </Dialog.Title>
        
        <Dialog.Description size="2" mb="4">
          {captain ? "Update captain information" : "Create a new delivery captain"}
        </Dialog.Description>

        <ScrollArea style={{ maxHeight: "70vh" }} scrollbars="vertical">
          <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Personal Information</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Profile Image URL
                </label>
                <input
                  type="url"
                  value={formData.profileImage}
                  onChange={(e) => handleInputChange("profileImage", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Vehicle Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <Car className="w-5 h-5" />
                Vehicle Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vehicle Type *
                </label>
                <select
                  required
                  value={formData.vehicleType}
                  onChange={(e) => handleInputChange("vehicleType", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  {vehicleTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vehicle Number *
                </label>
                <input
                  type="text"
                  required
                  value={formData.vehicleNumber}
                  onChange={(e) => handleInputChange("vehicleNumber", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Driving License URL *
                </label>
                <input
                  type="url"
                  required
                  value={formData.drivingLicense}
                  onChange={(e) => handleInputChange("drivingLicense", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vehicle Registration URL *
                </label>
                <input
                  type="url"
                  required
                  value={formData.vehicleRegistration}
                  onChange={(e) => handleInputChange("vehicleRegistration", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Insurance URL *
                </label>
                <input
                  type="url"
                  required
                  value={formData.insurance}
                  onChange={(e) => handleInputChange("insurance", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Bank Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account Holder Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.bankDetails.accountHolderName}
                  onChange={(e) => handleBankDetailsChange("accountHolderName", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account Number *
                </label>
                <input
                  type="text"
                  required
                  value={formData.bankDetails.accountNumber}
                  onChange={(e) => handleBankDetailsChange("accountNumber", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Routing Number *
                </label>
                <input
                  type="text"
                  required
                  value={formData.bankDetails.routingNumber}
                  onChange={(e) => handleBankDetailsChange("routingNumber", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Working Hours */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Working Hours
            </h3>
            <div className="space-y-3">
              {days.map((day) => (
                <div key={day} className="flex items-center gap-3">
                  <div className="w-20 text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{day}</div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.workingHours[day].isWorking}
                      onChange={(e) => handleWorkingHoursChange(day, "isWorking", e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Working</span>
                  </label>
                  {formData.workingHours[day].isWorking && (
                    <>
                      <input
                        type="time"
                        value={formData.workingHours[day].start}
                        onChange={(e) => handleWorkingHoursChange(day, "start", e.target.value)}
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="time"
                        value={formData.workingHours[day].end}
                        onChange={(e) => handleWorkingHoursChange(day, "end", e.target.value)}
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Flex gap="3" mt="6" pt="6" justify="end" style={{ borderTop: "1px solid var(--gray-5)" }}>
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button type="submit">
              {captain ? "Update Captain" : "Create Captain"}
            </Button>
          </Flex>
        </form>
        </ScrollArea>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default CaptainModal
